import { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, Search, Save, X, Upload, Loader2, UtensilsCrossed, Leaf, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { API_URL } from '@/config/api';

const DIETARY_TAGS = ['vegetarian', 'vegan', 'gluten-free', 'dairy-free', 'nut-free'];

const emptySide = {
  name: '',
  description: '',
  price: '',
  image_url: '',
  is_available: true,
  is_default: false,
  dietary_tags: []
};

export default function AdminSides() {
  const { token } = useAuth();
  const [sides, setSides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingSide, setEditingSide] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState(emptySide);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchSides();
  }, [token]);

  const fetchSides = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/sides`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setSides(await res.json());
      }
    } catch (error) {
      console.error('Error fetching sides:', error);
      toast.error('Failed to load sides');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleDietaryTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      dietary_tags: prev.dietary_tags.includes(tag)
        ? prev.dietary_tags.filter(t => t !== tag)
        : [...prev.dietary_tags, tag]
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Only JPEG, PNG, GIF, WEBP allowed');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Max 5MB allowed');
      return;
    }

    setImagePreview(URL.createObjectURL(file));
    setUploadingImage(true);

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await fetch(`${API_URL}/api/admin/menu/upload-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: uploadFormData
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `Upload failed (${response.status})`);
      }

      const data = await response.json();
      // Store the relative path — resolves correctly on any domain
      const imageUrl = data.image_url.startsWith('http') ? data.image_url : `${API_URL}${data.image_url}`;
      setFormData(prev => ({ ...prev, image_url: imageUrl }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error(error.message || 'Image upload failed. Check your connection.');
      setImagePreview(null);
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const isNew = !editingSide;
      const url = isNew
        ? `${API_URL}/api/admin/sides`
        : `${API_URL}/api/admin/sides/${editingSide.side_id}`;
      
      const method = isNew ? 'POST' : 'PUT';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          price: parseFloat(formData.price) || 0
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        if (response.status === 404) {
          throw new Error('This side no longer exists. Please refresh and try again.');
        }
        throw new Error(error.detail || `Failed to save (${response.status})`);
      }

      toast.success(isNew ? 'Side created successfully' : 'Side updated successfully');
      setIsDialogOpen(false);
      setEditingSide(null);
      setFormData(emptySide);
      setImagePreview(null);
      fetchSides();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (side) => {
    setEditingSide(side);
    setFormData({
      name: side.name,
      description: side.description || '',
      price: side.price?.toString() || '0',
      image_url: side.image_url || '',
      is_available: side.is_available ?? true,
      is_default: side.is_default ?? false,
      dietary_tags: side.dietary_tags || []
    });
    setImagePreview(side.image_url);
    setIsDialogOpen(true);
  };

  const handleDelete = async (sideId) => {
    if (!window.confirm('Are you sure you want to delete this side?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/sides/${sideId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete');

      toast.success('Side deleted');
      fetchSides();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const openNewSideDialog = () => {
    setEditingSide(null);
    setFormData(emptySide);
    setImagePreview(null);
    setIsDialogOpen(true);
  };

  const filteredSides = sides.filter(side =>
    side.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-sides">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-foreground flex items-center gap-2">
            <UtensilsCrossed className="h-6 w-6 text-primary" />
            Sides Management
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage side dishes that customers can add to their meals
          </p>
        </div>
        <Button onClick={openNewSideDialog} data-testid="add-side-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Side
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search sides..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="search-sides"
        />
      </div>

      {/* Sides Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredSides.map(side => (
          <Card key={side.side_id} className="overflow-hidden" data-testid={`side-card-${side.side_id}`}>
            {side.image_url && (
              <div className="aspect-video bg-muted relative">
                <img 
                  src={side.image_url} 
                  alt={side.name} 
                  className="w-full h-full object-cover"
                />
                {!side.is_available && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <Badge variant="destructive">Unavailable</Badge>
                  </div>
                )}
              </div>
            )}
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">{side.name}</h3>
                  {side.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                      {side.description}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <span className="font-bold text-primary">R{side.price?.toFixed(2)}</span>
                  {side.is_default && (
                    <Badge variant="secondary" className="ml-2 text-xs">Default</Badge>
                  )}
                </div>
              </div>
              
              {/* Dietary tags */}
              {side.dietary_tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-3">
                  {side.dietary_tags.map(tag => (
                    <Badge key={tag} variant="outline" className="text-xs">
                      <Leaf className="h-3 w-3 mr-1" />
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
              
              {/* Actions */}
              <div className="flex gap-2 mt-4 pt-4 border-t">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleEdit(side)}
                  className="flex-1"
                  data-testid={`edit-side-${side.side_id}`}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleDelete(side.side_id)}
                  className="text-destructive hover:text-destructive"
                  data-testid={`delete-side-${side.side_id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredSides.length === 0 && (
        <div className="text-center py-12">
          <UtensilsCrossed className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-semibold text-lg">No sides found</h3>
          <p className="text-muted-foreground">
            {searchQuery ? 'Try a different search term' : 'Add your first side dish'}
          </p>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>
              {editingSide ? 'Edit Side' : 'Add New Side'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Image Upload */}
            <div>
              <Label>Image</Label>
              <div className="mt-2">
                {(imagePreview || formData.image_url) ? (
                  <div className="relative aspect-video rounded-lg overflow-hidden bg-muted">
                    <img 
                      src={imagePreview || formData.image_url} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="absolute bottom-2 right-2"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {uploadingImage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                      Change
                    </Button>
                  </div>
                ) : (
                  <div 
                    className="aspect-video rounded-lg border-2 border-dashed flex flex-col items-center justify-center cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                    <span className="text-sm text-muted-foreground">Click to upload image</span>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>
            </div>

            {/* Name */}
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., Chips"
                required
                data-testid="side-name-input"
              />
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Brief description of the side dish"
                rows={2}
              />
            </div>

            {/* Price */}
            <div>
              <Label htmlFor="price">Extra Price (R)</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => handleChange('price', e.target.value)}
                placeholder="0.00"
                data-testid="side-price-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Price charged when customer adds this as an extra side
              </p>
            </div>

            {/* Dietary Tags */}
            <div>
              <Label>Dietary Tags</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {DIETARY_TAGS.map(tag => (
                  <Badge
                    key={tag}
                    variant={formData.dietary_tags.includes(tag) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => toggleDietaryTag(tag)}
                  >
                    {formData.dietary_tags.includes(tag) && <Check className="h-3 w-3 mr-1" />}
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Toggles */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Available</Label>
                  <p className="text-xs text-muted-foreground">Show this side to customers</p>
                </div>
                <Switch
                  checked={formData.is_available}
                  onCheckedChange={(checked) => handleChange('is_available', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Default Side</Label>
                  <p className="text-xs text-muted-foreground">Include in standard side options</p>
                </div>
                <Switch
                  checked={formData.is_default}
                  onCheckedChange={(checked) => handleChange('is_default', checked)}
                />
              </div>
            </div>

            <DialogFooter className="gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving || !formData.name}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                {editingSide ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
